// src/components/ui/sections/PaymentSettingsSection/PaymentSettingsSectionShadcn.jsx
import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  CheckCircle2,
  AlertTriangle,
  Clock,
  ExternalLink,
  RefreshCw,
  Unlink,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';

// shadcn components
import { Button } from '../../button';
import { Input } from '../../input';
import { Label } from '../../label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../card';
import { Alert, AlertDescription } from '../../alert';
import { Badge } from '../../badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../collapsible';

import { cn } from '../../../../utils/cn';
import { useStripe } from '../../../../hooks/useStripe';

const PaymentSettingsSectionShadcn = ({
  hasChanges = false,
  className = ''
}) => {
  // Use the Stripe hook (retaining all existing functionality)
  const {
    // Connection data
    stripeConnection,
    loading,
    error,
    status,
    statusMessage,
    nextAction,
    canAcceptPayments,
    needsSetup,

    // Actions
    loadStripeConnectionStatus,
    startExpressSetup,
    createOnboardingLink,
    checkOnboardingStatus,
    getExpressDashboardLink,
    disconnectStripeAccount,
    clearError,

    // States
    setupInProgress,
    onboardingUrl,
    dashboardUrl,
    refreshingStatus,
    disconnecting,
    generatingDashboardLink
  } = useStripe();

  // Local UI state
  const [setupEmail, setSetupEmail] = useState('');
  const [setupCountry, setSetupCountry] = useState('US');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAccountDetails, setShowAccountDetails] = useState(false);

  // Load initial status on mount (retain existing behavior)
  useEffect(() => {
    loadStripeConnectionStatus();
  }, []);

  // Handle onboarding URL redirect (retain existing behavior)
  useEffect(() => {
    if (onboardingUrl) {
      console.log('Redirecting to Stripe onboarding:', onboardingUrl);
      setTimeout(() => {
        window.location.href = onboardingUrl;
      }, 2000);
    }
  }, [onboardingUrl]);

  // Handle dashboard URL opening (retain existing behavior)
  useEffect(() => {
    if (dashboardUrl) {
      console.log('Opening Stripe dashboard:', dashboardUrl);
      window.open(dashboardUrl, '_blank');
    }
  }, [dashboardUrl]);

  // Event handlers (retain all existing)
  const handleStartSetup = (e) => {
    e.preventDefault();
    if (!setupEmail.trim()) {
      alert('Please enter a business email address');
      return;
    }
    startExpressSetup(setupEmail, setupCountry);
  };

  const handleContinueOnboarding = () => {
    createOnboardingLink();
  };

  const handleRefreshStatus = () => {
    checkOnboardingStatus();
  };

  const handleOpenDashboard = () => {
    getExpressDashboardLink();
  };

  const handleDisconnect = () => {
    if (confirm('Are you sure you want to disconnect your payment account? This will disable payment processing.')) {
      disconnectStripeAccount();
    }
  };

  // Status configuration helper
  const getStatusConfig = () => {
    switch (status) {
      case 'active':
        return {
          variant: 'default',
          icon: CheckCircle2,
          color: 'text-emerald-500'
        };
      case 'restricted':
        return {
          variant: 'destructive',
          icon: AlertTriangle,
          color: 'text-red-500'
        };
      case 'pending_onboarding':
        return {
          variant: 'default',
          icon: Clock,
          color: 'text-amber-500'
        };
      case 'charges_only':
        return {
          variant: 'secondary',
          icon: Info,
          color: 'text-blue-500'
        };
      default:
        return {
          variant: 'secondary',
          icon: CreditCard,
          color: 'text-muted-foreground'
        };
    }
  };

  // Helper to render requirements
  const renderRequirements = () => {
    if (!stripeConnection?.requirements_pending) return null;

    const requirements = stripeConnection.requirements_pending;
    const currentlyDue = requirements.currently_due || [];
    const pastDue = requirements.past_due || [];

    if (currentlyDue.length === 0 && pastDue.length === 0) return null;

    const getRequirementText = (req) => {
      const requirementMap = {
        'individual.verification.document': 'Identity verification document',
        'individual.id_number': 'ID number (SSN/EIN)',
        'individual.address.city': 'Address - City',
        'individual.address.line1': 'Address - Street address',
        'individual.address.postal_code': 'Address - ZIP code',
        'individual.address.state': 'Address - State',
        'individual.dob.day': 'Date of birth - Day',
        'individual.dob.month': 'Date of birth - Month',
        'individual.dob.year': 'Date of birth - Year',
        'individual.email': 'Email address',
        'individual.first_name': 'First name',
        'individual.last_name': 'Last name',
        'individual.phone': 'Phone number',
        'individual.ssn_last_4': 'Last 4 digits of SSN',
        'business_profile.mcc': 'Business category',
        'business_profile.url': 'Business website',
        'external_account': 'Bank account for payouts',
        'tos_acceptance.date': 'Terms of service acceptance',
        'tos_acceptance.ip': 'Terms acceptance IP verification',
        'settings.payments.statement_descriptor': 'Payment statement descriptor'
      };
      return requirementMap[req] || req.replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    return (
      <Alert variant="destructive" className="mt-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <div className="font-medium mb-2">Required Information:</div>
          <div className="space-y-1">
            {pastDue.map((req, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm">{getRequirementText(req)} <span className="text-red-400 font-medium">(Past Due)</span></span>
              </div>
            ))}
            {currentlyDue.filter(req => !pastDue.includes(req)).map((req, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm">{getRequirementText(req)}</span>
              </div>
            ))}
          </div>
        </AlertDescription>
      </Alert>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className={cn("space-y-6 p-6", className)}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment Processing
            </CardTitle>
            <CardDescription>Loading payment settings...</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-border border-t-primary rounded-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  return (
    <div className={cn("space-y-6 p-6", className)}>
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <Button variant="ghost" size="sm" onClick={clearError}>
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Success Alert for Redirect */}
      {onboardingUrl && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            Redirecting to Stripe to complete verification...
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      {status === 'disconnected' ? (
        /* Disconnected State */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Set Up Payment Processing
            </CardTitle>
            <CardDescription>
              Enable credit card payments on your invoices by connecting with Stripe
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleStartSetup} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="business_email">Business Email</Label>
                <Input
                  id="business_email"
                  type="email"
                  value={setupEmail}
                  onChange={(e) => setSetupEmail(e.target.value)}
                  placeholder="business@example.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Business Country</Label>
                <Select value={setupCountry} onValueChange={setSetupCountry}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="CA">Canada</SelectItem>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                    <SelectItem value="AU">Australia</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={setupInProgress}
                  size="lg"
                >
                  {setupInProgress ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                      Setting up...
                    </>
                  ) : (
                    'Connect with Stripe'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        /* Connected State */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment Processing
            </CardTitle>
            <CardDescription>
              Manage your Stripe payment connection and settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status Display */}
            <Alert variant={statusConfig.variant}>
              <StatusIcon className={cn("h-4 w-4", statusConfig.color)} />
              <AlertDescription>
                <div className="font-medium">{statusMessage}</div>
                <div className="text-sm text-muted-foreground">{nextAction}</div>
              </AlertDescription>
            </Alert>

            {/* Requirements (if any) */}
            {status === 'restricted' && renderRequirements()}

            {/* Payment Features Grid (for active accounts) */}
            {status === 'active' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Badge variant={canAcceptPayments ? "default" : "secondary"}>
                    {canAcceptPayments ? 'Enabled' : 'Disabled'}
                  </Badge>
                  <span className="text-sm">Credit Card Payments</span>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={canAcceptPayments ? "default" : "secondary"}>
                    {canAcceptPayments ? 'Enabled' : 'Disabled'}
                  </Badge>
                  <span className="text-sm">Online Invoice Payments</span>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={stripeConnection?.payouts_enabled ? "default" : "secondary"}>
                    {stripeConnection?.payouts_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                  <span className="text-sm">Automatic Payouts</span>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={canAcceptPayments ? "default" : "secondary"}>
                    {canAcceptPayments ? 'Enabled' : 'Disabled'}
                  </Badge>
                  <span className="text-sm">Payment Notifications</span>
                </div>
              </div>
            )}

            {/* Primary Actions */}
            <div className="flex flex-wrap gap-3">
              {status === 'active' && (
                <Button
                  onClick={handleOpenDashboard}
                  disabled={generatingDashboardLink}
                  variant="outline"
                >
                  {generatingDashboardLink ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <ExternalLink className="w-4 h-4 mr-2" />
                  )}
                  Open Dashboard
                </Button>
              )}

              {(status === 'pending_onboarding' || status === 'restricted' || status === 'charges_only') && (
                <Button
                  onClick={handleContinueOnboarding}
                  disabled={setupInProgress}
                  variant={status === 'restricted' ? 'destructive' : 'default'}
                >
                  {setupInProgress ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  ) : null}
                  {status === 'restricted' ? 'Complete Verification' :
                   status === 'charges_only' ? 'Complete Bank Setup' :
                   'Continue Setup'}
                </Button>
              )}

              <Button
                onClick={handleRefreshStatus}
                disabled={refreshingStatus}
                variant="outline"
                size="sm"
              >
                {refreshingStatus ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh
              </Button>
            </div>

            {/* Advanced Section */}
            {(status === 'active' || showAdvanced) && (
              <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    Advanced Options
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-4">
                  <div className="flex flex-wrap gap-3">
                    {status === 'active' && (
                      <Button
                        onClick={handleDisconnect}
                        disabled={disconnecting}
                        variant="outline"
                        className="text-red-500 hover:text-red-400 border-red-500/30"
                      >
                        {disconnecting ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                        ) : (
                          <Unlink className="w-4 h-4 mr-2" />
                        )}
                        Disconnect Account
                      </Button>
                    )}

                    <Collapsible open={showAccountDetails} onOpenChange={setShowAccountDetails}>
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="flex items-center gap-2">
                          {showAccountDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          Account Details
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {stripeConnection && (
                          <Card className="mt-4">
                            <CardContent className="p-3 text-xs text-muted-foreground space-y-1">
                              <div>Account ID: {stripeConnection.account_id}</div>
                              <div>Type: {stripeConnection.account_type}</div>
                              <div>Charges: {stripeConnection.charges_enabled ? 'Enabled' : 'Disabled'}</div>
                              <div>Payouts: {stripeConnection.payouts_enabled ? 'Enabled' : 'Disabled'}</div>
                              {stripeConnection.last_status_check && (
                                <div>Last Updated: {new Date(stripeConnection.last_status_check).toLocaleString()}</div>
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentSettingsSectionShadcn;